// src/components/solutions/SolutionsServices.tsx
import React from 'react';
import { ContentCard } from '@dopomogai/ui';
import { renderIcon } from '@dopomogai/utils';
import { CheckCircle } from 'lucide-react';

interface Service {
  icon: string;
  title: string;
  description: string;
  idealForTitle: string;
  idealForItems: string[];
  cta: string;
  howWeEmbedTitle?: string;
  howWeEmbedItems?: { icon: string, text: string }[];
}

interface ServicesData {
  headline: string;
  employees: Service;
  apps: Service;
}

interface SolutionsServicesProps {
  servicesData: ServicesData;
  onAction: (actionType: string, payload?: any) => void;
}

export const SolutionsServices: React.FC<SolutionsServicesProps> = ({ servicesData, onAction }) => {
  const renderServiceCard = (service: Service, visual: React.ReactNode, actionPayload: any) => (
    <ContentCard
      icon={renderIcon(service.icon, { size: 32, className: 'text-gray-800' })}
      iconContainerClassName="p-3 bg-slate-100 border-2 border-black rounded-lg w-fit"
      title={service.title}
      visualContent={visual}
      text={
        <div className="space-y-4">
          <p>{service.description}</p>
          <h4 className="font-bold text-gray-800 pt-2">{service.idealForTitle}</h4>
          <ul className="space-y-2">
            {service.idealForItems.map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle size={18} className="text-emerald-500 mt-0.5 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          {service.howWeEmbedTitle && service.howWeEmbedItems && (
            <>
              <h4 className="font-bold text-gray-800 pt-2">{service.howWeEmbedTitle}</h4>
              <ul className="space-y-2">
                {service.howWeEmbedItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    {renderIcon(item.icon, { size: 18, className: 'text-indigo-500 mt-0.5 shrink-0' })}
                    <span>{item.text}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      }
      cta={{
        text: service.cta,
        onClick: () => onAction('NAVIGATE_TO_PAGE', actionPayload),
        variant: 'primary',
      }}
    />
  );

  return (
    <section className="bg-slate-100 py-16 md:py-24">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center">{servicesData.headline}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-12">
          {renderServiceCard(
            servicesData.employees,
            <img src="https://placehold.co/600x400/0f172a/FFFFFF?text=AI+Employee" alt="AI Employee Mockup" className="w-full h-full object-cover"/>,
            { path: '/contact?subject=CustomAIEmployee' }
          )}
          {renderServiceCard(
            servicesData.apps,
            <img src="https://placehold.co/600x400/6366f1/FFFFFF?text=Embedded+AI" alt="Embedded AI Mockup" className="w-full h-full object-cover"/>,
            { path: '/contact?subject=EmbedAI' }
          )}
        </div>
      </div>
    </section>
  );
};

